<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Banners encontrados
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('banner:Create')"
                    color="success"
                    size="large"
                    @click="$router.push('/app/banners/new')"
                >
                    <span class="hidden md:inline">Nuevo banner</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-2">
            <div class="w-full my-4">
                <banners-filters v-model="query" @enter="reloadItems" />
                <div class="flex justify-center mt-2">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('banner:Create')
                        "
                        icon="🗂️"
                        message="Clonar banner"
                        @click="cloneSelected"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="single"
                responsive
                selectable
                outlined
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
            >
                <template #cell(id)="data">
                    <router-link
                        :to="`/app/banners/detail/${data.item.id}`"
                        class="text-center btn btn-stack-primary p-1"
                    >
                        <small class="ml-1">
                            {{ data.item.active ? "🟢" : "⚪" }}
                        </small>
                        <small>
                            {{ data.item.id }}
                        </small>
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name | cut(36) }}
                    </span>
                </template>

                <template #cell(line)="data">
                    <a
                        v-if="data.item.line"
                        :href="`/app/lines/detail/${data.item.line.slug}`"
                        target="_blank"
                        rel="noopener"
                    >
                        <span class="text-center">
                            {{ data.item.line.name }}
                        </span>
                    </a>
                    <span v-else> Root </span>
                </template>
                <template #cell(sequence)="data">
                    <span class="text-center">
                        {{ data.item.sequence }}
                    </span>
                </template>
                <template #cell(updatedAt)="data">
                    <span class="text-center">
                        {{ data.item.updatedAt | date }}
                    </span>
                </template>
            </custom-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import BannersFilters from "@/components/banners/BannersFilters.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "ProductsIndex",
    components: { BannersFilters, TooltipButton, CustomTable },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        query: {},
        itemsCurrent: [],
        $fields: [
            { key: "id", label: "id", sortable: false },
            { key: "name", label: "Nombre", sortable: true },
            { key: "line", label: "Categoría", sortable: true },
            { key: "sequence", label: "Orden", sortable: true },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "lineSlug", type: "string" },
            { key: "keyword", type: "string" }
        ],

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        selected: []
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("banner:List", "/app");
        this.listItems(true);
    },
    methods: {
        ...mapActions("banners", ["listBanners"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listBanners({
                    complete,
                    itemsByPage: this.pageSize,
                    page: this.currentPage,
                    ...this.query
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        cloneSelected() {
            window
                .open(`/app/banners/clone/${this.selected[0].id}`, "_blank")
                .focus();
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        }
    }
};
</script>
